import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { ClientOnly } from "~/components/ui/client-only";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { Tabs, TabsContent, TabsListPill, TabsTriggerPill } from "~/components/ui/tabs";
import { envConfig } from "~/lib/env-validation";
import { trackEvent } from "~/lib/gtag";
import { useAuth } from "~/lib/hooks/use-auth";

const loginSchema = z.object({
	email: z.string().email("Neplatná e-mailová adresa"),
	password: z.string().min(6, "Heslo musí mít alespoň 6 znaků"),
});

const forgotPasswordSchema = z.object({
	email: z.string().email("Neplatná e-mailová adresa"),
});

const signUpSchema = z
	.object({
		email: z.string().email("Neplatná e-mailová adresa"),
		password: z.string().min(6, "Heslo musí mít alespoň 6 znaků"),
		confirmPassword: z.string(),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Hesla se neshodují",
		path: ["confirmPassword"],
	});

type LoginFormData = z.infer<typeof loginSchema>;
type SignUpFormData = z.infer<typeof signUpSchema>;
type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const AUTH_ERROR_MESSAGES: Record<string, string> = {
	invalid_credentials: "Nesprávný e-mail nebo heslo.",
	email_not_confirmed: "E-mail zatím nebyl potvrzen. Zkontrolujte svou schránku.",
	user_already_exists: "Účet s tímto e-mailem už existuje. Zkuste se přihlásit.",
	over_request_rate_limit: "Příliš mnoho pokusů. Zkuste to prosím za chvíli.",
	weak_password: "Heslo je příliš slabé. Zvolte silnější heslo.",
};

function translateAuthError(error: { code?: string; message: string }): string {
	return (
		(error.code && AUTH_ERROR_MESSAGES[error.code]) ??
		"Přihlášení se nezdařilo. Zkuste to prosím znovu."
	);
}

function LoginForm({ onForgotPassword }: { onForgotPassword: () => void }) {
	const { signInWithEmail } = useAuth();
	const [error, setError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
	});

	async function onSubmit(data: LoginFormData) {
		setError(null);
		const { error } = await signInWithEmail(data.email, data.password);
		if (error) {
			setError(translateAuthError(error));
		} else {
			trackEvent("login", { method: "email" });
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<Label htmlFor="login-email">E-mail</Label>
				<Input id="login-email" type="email" placeholder="jan@firma.cz" {...register("email")} />
				{errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
			</div>

			<div className="flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<Label htmlFor="login-password">Heslo</Label>
					<button
						type="button"
						onClick={onForgotPassword}
						className="text-xs text-primary hover:underline"
					>
						Zapomněli jste heslo?
					</button>
				</div>
				<Input
					id="login-password"
					type="password"
					placeholder="••••••••"
					{...register("password")}
				/>
				{errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
			</div>

			{error && <p className="text-sm text-destructive">{error}</p>}

			<Button type="submit" className="w-full" disabled={isSubmitting}>
				{isSubmitting ? "Přihlašování..." : "Přihlásit se"}
			</Button>
		</form>
	);
}

function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
	const [error, setError] = useState<string | null>(null);
	const [sent, setSent] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<ForgotPasswordFormData>({
		resolver: zodResolver(forgotPasswordSchema),
	});

	async function onSubmit(data: ForgotPasswordFormData) {
		setError(null);
		try {
			const res = await fetch(`${envConfig.apiBaseUrl}/auth/password-reset`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: data.email }),
			});
			if (res.status === 429) {
				setError("Příliš mnoho pokusů. Zkuste to prosím později.");
				return;
			}
			if (!res.ok) {
				setError("Něco se pokazilo. Zkuste to prosím znovu.");
				return;
			}
			setSent(true);
		} catch {
			setError("Něco se pokazilo. Zkuste to prosím znovu.");
		}
	}

	if (sent) {
		return (
			<div className="flex flex-col gap-4">
				<p className="text-sm text-center text-muted-foreground">
					Pokud k tomuto e-mailu existuje účet, poslali jsme vám odkaz pro obnovení hesla.
					Zkontrolujte svou schránku.
				</p>
				<Button type="button" variant="outline" className="w-full" onClick={onBack}>
					Zpět na přihlášení
				</Button>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<p className="text-sm text-muted-foreground">
				Zadejte svůj e-mail a pošleme vám odkaz pro nastavení nového hesla.
			</p>

			<div className="flex flex-col gap-2">
				<Label htmlFor="forgot-email">E-mail</Label>
				<Input id="forgot-email" type="email" placeholder="jan@firma.cz" {...register("email")} />
				{errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
			</div>

			{error && <p className="text-sm text-destructive">{error}</p>}

			<Button type="submit" className="w-full" disabled={isSubmitting}>
				{isSubmitting ? "Odesílání..." : "Odeslat odkaz"}
			</Button>
			<button
				type="button"
				onClick={onBack}
				className="text-sm text-muted-foreground hover:text-foreground transition-colors"
			>
				← Zpět na přihlášení
			</button>
		</form>
	);
}

function SignUpForm() {
	const { signUp } = useAuth();
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<SignUpFormData>({
		resolver: zodResolver(signUpSchema),
	});

	async function onSubmit(data: SignUpFormData) {
		setError(null);
		const { error } = await signUp(data.email, data.password);
		if (error) {
			setError(translateAuthError(error));
			return;
		}
		trackEvent("sign_up", { method: "email" });
		setSuccess(true);
	}

	if (success) {
		return (
			<p className="text-sm text-center text-muted-foreground">
				Zkontrolujte svůj e-mail pro potvrzovací odkaz.
			</p>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<Label htmlFor="signup-email">E-mail</Label>
				<Input id="signup-email" type="email" placeholder="jan@firma.cz" {...register("email")} />
				{errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
			</div>

			<div className="flex flex-col gap-2">
				<Label htmlFor="signup-password">Heslo</Label>
				<Input
					id="signup-password"
					type="password"
					placeholder="••••••••"
					{...register("password")}
				/>
				{errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
			</div>

			<div className="flex flex-col gap-2">
				<Label htmlFor="signup-confirm">Potvrzení hesla</Label>
				<Input
					id="signup-confirm"
					type="password"
					placeholder="••••••••"
					{...register("confirmPassword")}
				/>
				{errors.confirmPassword && (
					<p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
				)}
			</div>

			{error && <p className="text-sm text-destructive">{error}</p>}

			<Button type="submit" className="w-full" disabled={isSubmitting}>
				{isSubmitting ? "Vytváření účtu..." : "Vytvořit účet"}
			</Button>
		</form>
	);
}

function LoginPage() {
	const { signInWithOAuth } = useAuth();
	const [forgotPassword, setForgotPassword] = useState(false);

	if (forgotPassword) {
		return (
			<div className="min-h-screen flex items-center justify-center px-4">
				<div className="w-full max-w-sm flex flex-col gap-6">
					<div className="flex flex-col items-center gap-3">
						<img src="/logo.png" alt="FirmIndex" className="h-10" />
						<p className="text-sm text-muted-foreground">Obnovení hesla</p>
					</div>
					<ForgotPasswordForm onBack={() => setForgotPassword(false)} />
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center px-4">
			<div className="w-full max-w-sm flex flex-col gap-6">
				<div className="flex flex-col items-center gap-3">
					<img src="/logo.png" alt="FirmIndex" className="h-10" />
					<p className="text-sm text-muted-foreground">Přihlaste se ke svému účtu</p>
				</div>

				<Tabs defaultValue="login" className="w-full">
					<TabsListPill className="w-full">
						<TabsTriggerPill value="login">Přihlášení</TabsTriggerPill>
						<TabsTriggerPill value="signup">Registrace</TabsTriggerPill>
					</TabsListPill>

					<TabsContent value="login">
						<LoginForm onForgotPassword={() => setForgotPassword(true)} />
					</TabsContent>

					<TabsContent value="signup">
						<SignUpForm />
					</TabsContent>
				</Tabs>

				<div className="flex items-center gap-3">
					<Separator className="flex-1" />
					<span className="text-xs text-muted-foreground">nebo</span>
					<Separator className="flex-1" />
				</div>

				<Button variant="outline" className="w-full" onClick={() => signInWithOAuth("google")}>
					<GoogleIcon />
					Pokračovat přes Google
				</Button>
			</div>
		</div>
	);
}

export default function Login() {
	return (
		<ClientOnly
			fallback={
				<div className="min-h-screen flex items-center justify-center">
					<div className="w-full max-w-sm flex flex-col items-center gap-3">
						<img src="/logo.png" alt="FirmIndex" className="h-10" />
						<p className="text-sm text-muted-foreground">Načítání...</p>
					</div>
				</div>
			}
		>
			<LoginPage />
		</ClientOnly>
	);
}

function GoogleIcon() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
			<path
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
				fill="#4285F4"
			/>
			<path
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
				fill="#34A853"
			/>
			<path
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
				fill="#FBBC05"
			/>
			<path
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
				fill="#EA4335"
			/>
		</svg>
	);
}
